/**
 * Transient notifications.
 *
 * A toast is the right place for "message failed to send" and the wrong place
 * for anything the user must act on, because it takes itself away again. The
 * region is polite rather than assertive: an arriving toast waits for the
 * screen reader to finish the sentence it is on instead of cutting into it.
 */

import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import type { ReactElement, ReactNode } from 'react'

import { IconButton } from './Button'
import styles from './Toast.module.css'

function cx(...values: Array<string | false | null | undefined>): string {
  return values.filter((value): value is string => typeof value === 'string' && value !== '').join(' ')
}

export type ToastTone = 'info' | 'success' | 'warning' | 'error'

export interface ToastAction {
  label: string
  onClick: () => void
}

export interface ToastOptions {
  tone?: ToastTone
  /** Milliseconds on screen. Zero keeps it until it is dismissed by hand. */
  duration?: number
  action?: ToastAction
}

/** Failures are read more slowly than confirmations, so they linger. */
function defaultDuration(tone: ToastTone): number {
  return tone === 'error' ? 9000 : tone === 'warning' ? 7000 : 5000
}

function DismissIcon(): ReactElement {
  return (
    <svg viewBox="0 0 16 16" fill="none" aria-hidden="true" focusable="false">
      <path d="m4 4 8 8M12 4l-8 8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  )
}

// ── Presentation ────────────────────────────────────────────────────────────

export interface ToastProps {
  message: string
  tone?: ToastTone
  /** Milliseconds until `onDismiss` fires. Zero or Infinity disables it. */
  duration?: number
  action?: ToastAction
  onDismiss?: () => void
  className?: string
}

export function Toast({
  message,
  tone = 'info',
  duration = 0,
  action,
  onDismiss,
  className,
}: ToastProps): ReactElement {
  const [paused, setPaused] = useState(false)

  // Held in a ref so that a parent re-render — another toast arriving, say —
  // does not restart this toast's countdown.
  const dismissRef = useRef(onDismiss)
  useEffect(() => {
    dismissRef.current = onDismiss
  })

  useEffect(() => {
    if (paused || !Number.isFinite(duration) || duration <= 0) return undefined

    const timer = window.setTimeout(() => dismissRef.current?.(), duration)
    return () => window.clearTimeout(timer)
  }, [duration, paused])

  return (
    <div
      className={cx(styles.toast, styles[tone], className)}
      onPointerEnter={() => setPaused(true)}
      onPointerLeave={() => setPaused(false)}
      onFocusCapture={() => setPaused(true)}
      onBlurCapture={() => setPaused(false)}
    >
      <p className={styles.message}>{message}</p>
      {action !== undefined ? (
        <button type="button" className={styles.action} onClick={action.onClick}>
          {action.label}
        </button>
      ) : null}
      {onDismiss !== undefined ? (
        <IconButton label="Dismiss" size="sm" onClick={onDismiss} className={styles.dismiss}>
          <DismissIcon />
        </IconButton>
      ) : null}
    </div>
  )
}

// ── Provider ────────────────────────────────────────────────────────────────

interface ToastRecord {
  id: string
  message: string
  tone: ToastTone
  duration: number
  action: ToastAction | undefined
}

export interface ToastApi {
  show(message: string, options?: ToastOptions): string
  info(message: string, options?: Omit<ToastOptions, 'tone'>): string
  success(message: string, options?: Omit<ToastOptions, 'tone'>): string
  warning(message: string, options?: Omit<ToastOptions, 'tone'>): string
  error(message: string, options?: Omit<ToastOptions, 'tone'>): string
  dismiss(id: string): void
  dismissAll(): void
}

const ToastContext = createContext<ToastApi | null>(null)

/** Beyond this the stack stops being readable, so the oldest is dropped. */
const MAX_VISIBLE = 4

export function ToastProvider({ children }: { children: ReactNode }): ReactElement {
  const [toasts, setToasts] = useState<readonly ToastRecord[]>([])
  const nextId = useRef(0)

  const dismiss = useCallback((id: string): void => {
    setToasts((current) => current.filter((toast) => toast.id !== id))
  }, [])

  const dismissAll = useCallback((): void => {
    setToasts([])
  }, [])

  const show = useCallback((message: string, options?: ToastOptions): string => {
    const tone = options?.tone ?? 'info'
    nextId.current += 1
    const id = `relaii-toast-${nextId.current}`

    setToasts((current) =>
      [
        ...current,
        { id, message, tone, duration: options?.duration ?? defaultDuration(tone), action: options?.action },
      ].slice(-MAX_VISIBLE),
    )

    return id
  }, [])

  const api = useMemo<ToastApi>(
    () => ({
      show,
      info: (message, options) => show(message, { ...options, tone: 'info' }),
      success: (message, options) => show(message, { ...options, tone: 'success' }),
      warning: (message, options) => show(message, { ...options, tone: 'warning' }),
      error: (message, options) => show(message, { ...options, tone: 'error' }),
      dismiss,
      dismissAll,
    }),
    [show, dismiss, dismissAll],
  )

  return (
    <ToastContext.Provider value={api}>
      {children}
      <div className={styles.viewport}>
        <ol className={styles.list} aria-live="polite" aria-label="Notifications">
          {toasts.map((toast) => (
            <li key={toast.id} className={styles.item}>
              <ToastListItem record={toast} onDismiss={dismiss} />
            </li>
          ))}
        </ol>
      </div>
    </ToastContext.Provider>
  )
}

/** Exists only to give each toast a dismiss callback that is stable per id. */
function ToastListItem({
  record,
  onDismiss,
}: {
  record: ToastRecord
  onDismiss: (id: string) => void
}): ReactElement {
  const dismiss = useCallback(() => onDismiss(record.id), [onDismiss, record.id])

  return (
    <Toast
      message={record.message}
      tone={record.tone}
      duration={record.duration}
      action={record.action}
      onDismiss={dismiss}
    />
  )
}

export function useToast(): ToastApi {
  const api = useContext(ToastContext)

  if (api === null) {
    throw new Error('useToast must be used inside <ToastProvider>.')
  }

  return api
}
